import { tokenDrift, type Mode } from './generaToken';

/*
 * I TOKEN DEL TENANT, SCRITTI DAVVERO — tutti, non cinque.
 *
 * ## Il difetto che chiude
 *
 * ⚠️ Fino al 05-09-2026 `applyBrandColors` scriveva **cinque** variabili — le quattro di Mantine
 * per il primario piu' `--mantine-color-secondary` — e **nessun `--*`**. Un'azienda che
 * sceglieva il verde otteneva bottoni verdi, testata verde e menu verde, e **marca in tutte le
 * migliaia di letture dipinte a mano**: avatar, riquadri-icona, hero, accenti dei KPI, il giorno
 * «oggi» del calendario. Il white-label si fermava alle primitive di Mantine, e nessuno lo
 * dichiarava — la pagina Branding prometteva un colore e ne consegnava un quinto.
 *
 * Qui i token li deriva il generatore da **marca + accento**, e si scrivono tutti.
 *
 * ## ⚠️ La trappola dello stile in linea, e perche' serve l'osservatore
 *
 * Queste variabili si scrivono nello `style` in linea di `<html>`, che **batte per cascata**
 * qualunque regola di un foglio — compreso il blocco `[data-theme='dark']` di `theme.css`.
 * Quindi scrivere i token del tema chiaro e lasciarli li' **spegnerebbe il tema scuro**: al
 * cambio di schema il foglio proverebbe a ridefinirli e perderebbe, e l'applicazione resterebbe
 * chiara con l'attributo che dice «scura».
 *
 * Non e' teorico ed e' invisibile finche' il tema scuro e' sospeso (`TEMA_SCURO_ABILITATO`),
 * cioe' **oggi non si vedrebbe** e si vedrebbe il giorno della riaccensione, quando nessuno
 * starebbe piu' guardando questo file. Da qui l'osservatore sull'attributo: al cambio di schema
 * i token si **riderivano** nel modo nuovo.
 */

/** Le variabili scritte, per poterle togliere tutte quando il tenant cambia o si esce. */
let written: string[] = [];
let observer: MutationObserver | null = null;
let last: { brand: string; accent: string } | null = null;

const ATTRIBUTE = 'data-mantine-color-scheme';

function currentMode(): Mode {
  return document.documentElement.getAttribute(ATTRIBUTE) === 'dark' ? 'dark' : 'light';
}

/**
 * Scrive i token derivati sullo `<html>`.
 *
 * ⚠️ Qui c'era un ciclo su **due** nomi — `--x` e `--mw-x` — perche' i due sistemi di token
 * convivevano e andavano emessi dalla stessa formula per non divergere (era il difetto che li
 * aveva visti divergere sul verde e sull'ambra). Il rinomino della tavolozza ha poi tolto il
 * prefisso `--mw-` da tutte le letture, e ha appiattito anche questo ciclo: scriveva **due volte
 * lo stesso nome**, e il commento accanto elencava «`--x` e `--x`» senza che nessuno lo notasse.
 *
 * Verificato prima di togliere il ciclo: nel repo non resta nessun `var(--mw-…)` da servire.
 * Il ciclo era innocuo — scrivere la stessa variabile due volte non fa danno — ma un commento
 * che descrive un meccanismo che non c'e' piu' e' peggio di nessun commento.
 */
function write(brand: string, accent: string): void {
  const root = document.documentElement;
  const token = tokenDrift(brand, accent, currentMode());
  const names: string[] = [];
  for (const [nome, value] of Object.entries(token)) {
    const variable = `--${nome}`;
    root.style.setProperty(variable, value);
    names.push(variable);
  }
  written = names;
}

/**
 * Deriva e applica l'intera tavolozza dai due colori scelti dall'azienda.
 *
 * `marca` e `accento` sono la stessa coppia che arriva a `applyBrandColors`: il primario e il
 * secondario del tenant. Se il secondario manca si usa il primario — il generatore sa derivare
 * un accento leggibile da un colore solo, ed e' meglio di un accento fisso che con certe marche
 * stona.
 */
export function applyTokenTenant(marca: string, accento?: string | null): void {
  last = { brand: marca, accent: accento || marca };
  write(last.brand, last.accent);

  if (!observer) {
    observer = new MutationObserver(() => {
      if (last) write(last.brand, last.accent);
    });
    observer.observe(document.documentElement, { attributeFilter: [ATTRIBUTE, 'data-theme'] });
  }
}

/** Toglie i token derivati: si torna all'identita' dei fogli. Serve uscendo dal portale. */
export function clearTokenTenant(): void {
  const root = document.documentElement;
  for (const v of written) root.style.removeProperty(v);
  written = [];
  last = null;
  observer?.disconnect();
  observer = null;
}
